import { Box, Button, FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, OutlinedInput, Paper, Typography, Card, CardContent, Divider, Grid, LinearProgress, Stack, Tooltip } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { updatePasswordByIdRefresh } from "../../actions/mangeAction";
// removed resetError import as toasts are dispatched directly on error
import { showSuccessToast, showErrorToast } from "../../slices/toastSlice";
import { Visibility, VisibilityOff, LockReset, CheckCircle, Cancel, AutoFixHigh, ContentCopy } from "@mui/icons-material";
import { useFormik } from 'formik';
import * as yup from 'yup';
import { t } from 'i18next';



const ChangePassword = () => {
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [copied, setCopied] = useState(false);
    const [copyFailed, setCopyFailed] = useState(false);
    const dispatch = useDispatch();
    const { mode } = useParams();
    const [searchParams] = useSearchParams()
    const id = searchParams.get('id');
    const location = useLocation();
    const { updated, added, hasError, errorMessage, loading } = useSelector((state) => state.manage) || {};
    const navigation = useNavigate();

    const validationSchema = yup.object({
        newPassword: yup
            .string()
            .required(t('Password is required'))
            .matches(/^(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/, t('Password must be at least 8 characters long and contain at least one special character')),
        confirmPassword: yup
            .string()
            .required(t('Please confirm the password'))
            .oneOf([yup.ref('newPassword')], t('Passwords must match')),
    });

    const formik = useFormik({
        initialValues: { newPassword: '', confirmPassword: '' },
        validationSchema,
        validateOnMount: true,
        onSubmit: (values) => {
            if (!id) return;
            dispatch(updatePasswordByIdRefresh({ mode, id, data: { newPassword: values.newPassword } }));
        },
    });

    const generatePassword = (length = 12) => {
        const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const lower = 'abcdefghijklmnopqrstuvwxyz';
        const digits = '0123456789';
        const special = "!@#$%^&*"; // align with backend allowlist
        const all = upper + lower + digits + special;
        const pick = (chars) => chars[Math.floor(Math.random() * chars.length)];
        let pwd = pick(upper) + pick(lower) + pick(digits) + pick(special);
        for (let i = pwd.length; i < length; i++) pwd += pick(all);
        const arr = pwd.split('');
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr.join('');
    };

    const handleGenerate = () => {
        const pw = generatePassword(14);
        // Atomically set both values, then validate once
        formik.setValues({ newPassword: pw, confirmPassword: pw }, false);
        formik.validateForm();
        formik.setTouched({ newPassword: true, confirmPassword: true }, false);
        setCopied(false);
    };

    const handleCopy = async () => {
        const text = formik.values.newPassword || '';
        if (!text) return;
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
                return;
            }
            throw new Error('Clipboard API not available');
        } catch (_) {
            const ok = fallbackCopyToClipboard(text);
            if (ok) {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            } else {
                setCopyFailed(true);
                setTimeout(() => setCopyFailed(false), 1500);
            }
        }
    };

    function fallbackCopyToClipboard(text) {
        try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.top = '-1000px';
            textArea.style.left = '-1000px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);
            return successful;
        } catch (e) {
            return false;
        }
    }

    const pwdScore = useMemo(() => {
        const v = formik.values.newPassword || '';
        let score = 0;
        if (v.length >= 8) score += 20;
        if (/[A-Z]/.test(v)) score += 20;
        if (/[a-z]/.test(v)) score += 20;
        if (/\d/.test(v)) score += 20;
        if (/[!@#$%^&*]/.test(v)) score += 20;
        return score;
    }, [formik.values.newPassword]);

    const handleBack = () => {
        navigation(`/${mode}/clientList` + location.search, {
            state: {
                showSuccess: false
            }
        })

    }
    useEffect(() => {
        if ((updated || added) && !hasError) {
            dispatch(showSuccessToast(t('Password updated successfully')));
            navigation(`/${mode}/clientList`, {
                state: {
                    showSuccess: true
                }
            })
        }
    }, [updated, added, hasError, navigation, mode, dispatch]);

    useEffect(() => {
        if (hasError && errorMessage) {
            dispatch(showErrorToast(errorMessage));
        }
    }, [hasError, errorMessage, dispatch]);
    const isSubmitting = loading === 'pending';

    return (
        <Paper variant="p1" sx={{ p: { xs: 1, md: 3 } }}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px rgba(11,60,93,0.08)', border: '1px solid #e9ecef' }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <LockReset sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('Change Password')}</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    <Box component="form" onSubmit={formik.handleSubmit} noValidate>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel htmlFor="newPassword">{t('New Password')}</InputLabel>
                                    <OutlinedInput
                                        id="newPassword"
                                        type={showNew ? 'text' : 'password'}
                                        name="newPassword"
                                        value={formik.values.newPassword}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <Tooltip title={t('Generate')}>
                                                    <IconButton aria-label="generate strong password" onClick={handleGenerate} edge="end">
                                                        <AutoFixHigh />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={copyFailed ? t('Copy failed') : (copied ? t('Copied') : t('Copy'))}>
                                                    <span>
                                                        <IconButton aria-label="copy password" onClick={handleCopy} edge="end" disabled={!formik.values.newPassword}>
                                                            <ContentCopy />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowNew((s) => !s)}
                                                    edge="end"
                                                >
                                                    {showNew ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                        label={t('New Password')}
                                    />
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        {formik.touched.newPassword && formik.errors.newPassword}
                                    </FormHelperText>
                                </FormControl>
                                <Box sx={{ mt: 1.5 }}>
                                    <LinearProgress variant="determinate" value={pwdScore} sx={{ height: 8, borderRadius: 1 }} />
                                    <Typography variant="caption" color="text.secondary">
                                        {t('Strength')}: {pwdScore <= 40 ? t('Weak') : pwdScore <= 80 ? t('Medium') : t('Strong')}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel htmlFor="confirmPassword">{t('Confirm Password')}</InputLabel>
                                    <OutlinedInput
                                        id="confirmPassword"
                                        type={showConfirm ? 'text' : 'password'}
                                        name="confirmPassword"
                                        value={formik.values.confirmPassword}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowConfirm((s) => !s)}
                                                    edge="end"
                                                >
                                                    {showConfirm ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                        label={t('Confirm Password')}
                                    />
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        {formik.touched.confirmPassword && formik.errors.confirmPassword}
                                    </FormHelperText>
                                </FormControl>
                                <Stack direction="column" spacing={0.5} sx={{ mt: 1.5 }}>
                                    <Req ok={formik.values.newPassword.length >= 8} label={t('At least 8 characters')} />
                                    <Req ok={/^[A-Za-z0-9!@#$%^&*]*$/.test(formik.values.newPassword)} label={t('Only letters, numbers, and !@#$%^&*')} />
                                    <Req ok={/[!@#$%^&*]/.test(formik.values.newPassword)} label={t('At least one of !@#$%^&*')} />
                                </Stack>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button variant="outlined" onClick={handleBack}>{t('Cancel')}</Button>
                            <Button variant="contained" type="submit" disabled={!formik.isValid || isSubmitting}>
                                {isSubmitting ? t('Updating...') : t('Update Password')}
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
            {/* Toasts are fired via showSuccessToast/showErrorToast effects */}
        </Paper>
    )
}

export default ChangePassword;

function Req({ ok, label }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {ok ? <CheckCircle fontSize="small" color="success" /> : <Cancel fontSize="small" sx={{ color: 'text.disabled' }} />}
            <Typography variant="caption" color={ok ? 'success.main' : 'text.secondary'}>{label}</Typography>
        </Box>
    );
}